import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { updateMyProfile } from "@/lib/journey.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Music2, BookOpen, Flame, KeyRound, Sparkles, ArrowRight } from "lucide-react";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/welcome")({
  head: () => ({
    meta: [
      { title: "Welcome to KAfrequency · Begin Your Journey" },
      { name: "description", content: "Begin your journey through KAfrequency — music, wisdom, the 47 Gates of Consciousness, and daily practice with Buddha KA." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Welcome to KAfrequency · Begin Your Journey" },
      { property: "og:description", content: "Music, wisdom, the 47 Gates of Consciousness, and daily practice. Enter the transmission." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/welcome") },
      { property: "og:site_name", content: "Buddha KA · KAfrequency" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Welcome to KAfrequency" },
      { name: "twitter:description", content: "Begin your journey through KAfrequency." },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/welcome") }],
  }),
  component: WelcomePage,
});

const PATHS = [
  { key: "music", label: "Begin with Music", icon: Music2, to: "/music" as const },
  { key: "wisdom", label: "Begin with Wisdom", icon: BookOpen, to: "/wisdom" as const },
  { key: "practice", label: "Begin with Practice", icon: Flame, to: "/activations" as const },
  { key: "gates", label: "Begin with the 47 Gates", icon: KeyRound, to: "/gates" as const },
  { key: "everything", label: "Explore Everything", icon: Sparkles, to: "/" as const },
];

function WelcomePage() {
  const [step, setStep] = useState(0);
  const save = useServerFn(updateMyProfile);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const choose = async (p: (typeof PATHS)[number]) => {
    setSaving(true);
    try {
      await save({ data: { onboarding_path: p.key, mark_onboarded: true } });
    } catch { /* not signed in yet — that's fine */ }
    navigate({ to: p.to });
  };

  return (
    <div className="min-h-dvh bg-obsidian text-foreground">
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:py-20">
        <Link to="/" className="text-sm text-gold underline underline-offset-4">Skip →</Link>

        {step === 0 && (
          <section className="mt-8 text-center">
            <h1 className="font-serif text-4xl text-gold-luminous sm:text-5xl">Welcome to KAfrequency</h1>
            <p className="mx-auto mt-4 max-w-xl text-foreground/70">
              A living temple of music, wisdom, and practice. Take a moment to see what's here — then choose where to begin.
            </p>
            <Button className="mt-8" onClick={() => setStep(1)}>Begin <ArrowRight size={16} className="ml-1" /></Button>
          </section>
        )}

        {step === 1 && (
          <section className="mt-8">
            <h2 className="font-serif text-2xl text-gold-luminous">Four currents, one river</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Card className="p-4"><h3 className="text-gold">Music</h3><p className="text-sm text-foreground/70">Buddha KA's frequencies — songs made to move consciousness.</p></Card>
              <Card className="p-4"><h3 className="text-gold">Wisdom</h3><p className="text-sm text-foreground/70">Teachings, books, and the 47 Gates of Consciousness.</p></Card>
              <Card className="p-4"><h3 className="text-gold">Practice</h3><p className="text-sm text-foreground/70">Daily activations, meditation, breath, discipline.</p></Card>
              <Card className="p-4"><h3 className="text-gold">Journey</h3><p className="text-sm text-foreground/70">Private journal, challenges, and your Life Journey Map.</p></Card>
            </div>
            <div className="mt-6 flex gap-2">
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button onClick={() => setStep(2)}>Continue</Button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="mt-8">
            <h2 className="font-serif text-2xl text-gold-luminous">Where would you like to begin?</h2>
            <p className="mt-2 text-sm text-foreground/70">Your choice personalizes what appears first. You can change it any time.</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {PATHS.map((p) => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.key}
                    type="button"
                    disabled={saving}
                    onClick={() => choose(p)}
                    className="flex items-center gap-3 rounded-md border hairline p-4 text-left transition-colors hover:border-gold"
                  >
                    <Icon className="text-gold" />
                    <span className="font-medium">{p.label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
